/* ============================================================
   SC INLINE CATALOGUE / D2C NADIA CONVERSATION ENGINE
   Prototype-only scripted version. Swap the response engine
   for a fetch() to the Nadia backend proxy when ready.
   ============================================================ */
(function (window, document) {
  'use strict';

  var SC = window.SC = window.SC || {};

  /* --- Storage --- */
  var STORAGE_KEY = 'sc_catalogue_request';
  function loadRequest() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
    catch (e) { return {}; }
  }
  function saveRequest(data) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (e) {}
  }
  function isCatalogueAlreadyRequested() {
    var d = loadRequest();
    return !!(d && d.submittedAt);
  }

  /* --- Validators --- */
  function looksLikeName(s) {
    return /^[A-Za-z][A-Za-z' \-]{0,60}$/.test((s || '').trim()) && s.trim().split(/\s+/).length >= 2;
  }
  /* Region awareness. window.SC_COUNTRY is set by the theme layout from
     Shopify localization (ISO code). UK asks for a postcode, US a ZIP
     code, everywhere else a postal code with lenient validation. */
  var SC_REGION = (function() {
    var c = (window.SC_COUNTRY || 'GB').toUpperCase();
    if (c === 'US') return 'US';
    if (c === 'GB' || c === 'UK') return 'UK';
    return 'INTL';
  })();
  function postcodeTerm() {
    return SC_REGION === 'US' ? 'ZIP code' : (SC_REGION === 'UK' ? 'postcode' : 'postal code');
  }
  function looksLikePostcode(s) {
    if (!s) return false;
    var ukPostcode = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i;
    var usZip = /\b\d{5}(-\d{4})?\b/;
    if (SC_REGION === 'US') return usZip.test(s);
    if (SC_REGION === 'UK') return ukPostcode.test(s);
    /* International: any plausible postal token (3+ word characters) */
    return /[A-Za-z0-9][A-Za-z0-9 \-]{2,10}/.test(s.trim());
  }
  function looksLikePhone(s) {
    var digits = (s || '').replace(/\D/g, '');
    return digits.length >= 9 && digits.length <= 14;
  }
  function lower(s) { return (s || '').toLowerCase().trim(); }
  function isAffirmative(s) { return /^(y|yes|yeah|yep|sure|ok|okay|alright|of course|please)\b/i.test(lower(s)); }
  function isNegative(s) { return /^(n|no|nope|nah|skip|not now|prefer not|rather not|no thanks?)\b/i.test(lower(s)); }

  /* --- Conversation state --- */
  var conv = null; // { mode, history, step, request, panelEl, scrollEl, inputEl,
                   //   d2cTurns, catalogueOffered, awaitingCatalogueYesNo }

  /* Switch a live conversation from D2C mode into catalogue form mode
     mid-stream. Keeps the same panel and history; just changes how the
     next user input is interpreted. */
  function switchToCatalogueMidStream() {
    if (!conv) return;
    conv.mode = 'catalogue';
    conv.step = 0;
    conv.awaitingCatalogueYesNo = false;
    askCurrentStep();
  }

  /* ============================================================
     CONVERSATION SCRIPT
     Each step: { id, ask(), accept(input, conv), advance(input, conv) }
     The script state machine drives the UI.
     ============================================================ */

  var CATALOGUE_STEPS = [
    {
      id: 'open',
      ask: function () {
        return [
          "What's your name?"
        ];
      },
      handle: function (input, c) {
        if (!looksLikeName(input)) {
          return reply("Just your first and last name will do. For example, 'Charlotte Bell'.");
        }
        c.request.name = input.trim();
        saveRequest(c.request);
        return advance(c, "Thank you, " + firstName(c.request.name) + ".");
      }
    },
    {
      id: 'address',
      ask: function (c) {
        return ["Where should we send the catalogue? The full address, including the " + postcodeTerm() + "."];
      },
      handle: function (input, c) {
        if (!looksLikePostcode(input)) {
          return reply("I just need a UK or US postal code in there. Could you paste the full address again?");
        }
        c.request.address = input.trim();
        saveRequest(c.request);
        return advance(c);
      }
    },
    {
      id: 'phone',
      ask: function () {
        return [
          "If we have a question about your order, can we reach you on a phone number? Totally optional. Just type 'skip' if you'd rather not."
        ];
      },
      handle: function (input, c) {
        if (isNegative(input) || /^skip\b/i.test(input.trim())) {
          c.request.phone = null;
          saveRequest(c.request);
          return advance(c, "No problem.");
        }
        if (!looksLikePhone(input)) {
          return reply("That doesn't quite look like a phone number to me. Try again, or type 'skip'.");
        }
        c.request.phone = input.trim();
        saveRequest(c.request);
        return advance(c, "Got it.");
      }
    },
    {
      id: 'timing',
      ask: function () {
        return [
          "When are you thinking of ordering a sofa?",
          "['Within the next month', 'In the next 3 months', '3 to 6 months', 'Just researching']"
        ];
      },
      options: ['Within the next month', 'In the next 3 months', '3 to 6 months', 'Just researching'],
      handle: function (input, c) {
        c.request.timing = input.trim();
        saveRequest(c.request);
        var msg;
        if (/within the next month|next month/i.test(input)) {
          msg = "Helpful to know. We'll make sure the catalogue gets to you quickly, and I can also help you start narrowing down today if you'd like.";
        } else if (/3 to 6 months|3-6|next 3 months|in the next 3/i.test(input)) {
          msg = "Plenty of time. The catalogue will give you the proper detail to mull over.";
        } else if (/researching|browsing/i.test(input)) {
          msg = "No rush at all. Read it at your own pace.";
        } else {
          msg = "Noted, thank you.";
        }
        return advance(c, msg);
      }
    },
    {
      id: 'size',
      ask: function () {
        return [
          "And the size you have in mind? You don't need to be exact, just a sense.",
          "['2-seater or smaller', '2.5-seater', '3-seater', '3.5 or 4-seater', 'Multiple pieces or corner setup', 'Modular sofa', 'Not sure yet']"
        ];
      },
      options: ['2-seater or smaller', '2.5-seater', '3-seater', '3.5 or 4-seater', 'Multiple pieces or corner setup', 'Modular sofa', 'Not sure yet'],
      handle: function (input, c) {
        c.request.size = input.trim();
        saveRequest(c.request);
        return advance(c);
      }
    },
    {
      id: 'rangePref',
      ask: function () {
        return [
          "Each of our six ranges has its own character. If you had to pick the one that catches your eye most, which would it be?",
          "[RANGE_THUMBS]"
        ];
      },
      handle: function (input, c) {
        c.request.rangePreference = input.trim();
        saveRequest(c.request);
        return advance(c);
      }
    },
    {
      id: 'handover',
      ask: function (c) {
        c.request.submittedAt = new Date().toISOString();
        saveRequest(c.request);

        /* Capture the catalogue request as a CRM lead, same shape as the other
           captures (fabric picker, room quiz, PDF). Non-blocking so a backend
           hiccup never breaks the conversation flow. */
        var leadPayload = {
          source: 'sc-catalogue-request',
          timestamp: c.request.submittedAt,
          lead: {
            name: c.request.name || null,
            address: c.request.address || null,
            phone: c.request.phone || null
          },
          interest: {
            timing: c.request.timing || null,
            size: c.request.size || null,
            range_preference: c.request.rangePreference || null
          }
        };
        /* lead payload intentionally not logged in production (PII) */
        try {
          fetch('https://cdn.sophieconran.com/api/leads/catalogue', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(leadPayload)
          }).catch(function(){});
        } catch (e) {}

        var name = firstName(c.request.name);
        var pick = c.request.rangePreference || 'that one';
        var rangeLine = pick && pick !== 'that one'
          ? "Lovely choice. The " + pick + " is a beautiful place to start."
          : "";
        var followUp = rangeLineFollowUp(pick);
        return [
          (rangeLine ? rangeLine : "Got it, " + name + "."),
          "Your catalogue is on its way. While we have a minute together, I'd love to help you start narrowing things down.",
          followUp
        ].filter(Boolean);
      },
      handle: function (input, c) {
        // Once we hand over, switch to D2C concierge mode and let Nadia keep the
        // conversation going as designer-concierge.
        c.mode = 'd2c';
        handleD2CTurn(input);
        return null;
      }
    }
  ];

  /* Tailored follow-up after the customer picks a range. Keeps Nadia in
     designer-concierge mode and surfaces the next useful question for
     each range. */
  function rangeLineFollowUp(rangeName) {
    var lower = (rangeName || '').toLowerCase();
    if (lower.indexOf('coco') > -1) {
      return "The Coco comes in three sizes from 2-seater to 3.5, and a full bespoke version. Would you like me to walk you through the fabric options, or talk about how the cushion fill behaves over time?";
    }
    if (lower.indexOf('barton') > -1) {
      return "The Barton is the deepest of the range. Properly sit-back-and-stay-there comfort. Tell me about the room, and I can think about whether the 2.5 or the 3 is the right size.";
    }
    if (lower.indexOf('henrietta') > -1) {
      return "Henrietta is our most heritage piece, modelled on a Victorian original. Roll arms, shaped back. Would it be sitting in a more formal room, or somewhere everyday?";
    }
    if (lower.indexOf('isobel') > -1) {
      return "The Isobel is slimmer-framed and our most affordable starting point. A great choice for snugs and second sitting rooms. Are you working to a particular budget?";
    }
    if (lower.indexOf('felix') > -1) {
      return "The Felix is our cleanest, most modern silhouette. Tight-back, low-arm. Would you like me to send a Felix sample, or talk through fabric directions first?";
    }
    if (lower.indexOf('sophia') > -1) {
      return "Sophia is fixed-cover, fixed back, mahogany leg. The most settled-in of the range. Tell me about the people who'll be using it most.";
    }
    return "Tell me a little about the room and the people who use it, and I can think about which range will sit best.";
  }

  /* General D2C opening — used by the lower Nadia introduction block.
     Lighter touch, no structured intake. */
  var D2C_OPENING = [
    "Hello. Tell me a little about what you're looking for, and I'll help you narrow it down.",
    "Are you thinking about a particular room, a particular size, or just having a browse?"
  ];

  /* ============================================================
     UI helpers
     ============================================================ */

  function firstName(full) {
    return (full || '').trim().split(/\s+/)[0] || 'there';
  }

  function reply(text) { return { messages: [text], advance: false }; }
  function advance(c, leadIn) {
    var m = leadIn ? [leadIn] : [];
    return { messages: m, advance: true };
  }

  function escapeHtml(s) {
    return (s || '').replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function bubble(side, text, opts) {
    opts = opts || {};
    var cls = 'sc-conv-bubble sc-conv-' + side + (opts.system ? ' sc-conv-system' : '');
    return '<div class="' + cls + '">' + (opts.html ? text : escapeHtml(text)) + '</div>';
  }

  function rangeThumbs() {
    var ranges = (window.SC && SC.ranges) ? SC.ranges.filter(function (r) { return !r.soldThrough; }) : [];
    var rangeImgs = (SC.images && SC.images.ranges) || {};
    var html = ranges.map(function (r) {
      var img = rangeImgs[r.handle] && rangeImgs[r.handle].src;
      var thumb = img
        ? '<img src="' + img + '" alt="' + escapeHtml(r.name) + '" loading="lazy" />'
        : '<div class="sc-conv-thumb-fallback"></div>';
      return '<button type="button" class="sc-conv-thumb" data-sc-conv-pick="' + escapeHtml(r.name) + '">' +
        thumb +
        '<span>' + escapeHtml(r.name) + '</span>' +
        '</button>';
    }).join('');
    return '<div class="sc-conv-thumbs">' + html + '</div>';
  }

  function renderOptions(options) {
    var html = options.map(function (opt) {
      return '<button type="button" class="sc-conv-chip" data-sc-conv-pick="' + escapeHtml(opt) + '">' +
        escapeHtml(opt) +
        '</button>';
    }).join('');
    return '<div class="sc-conv-chips">' + html + '</div>';
  }

  function appendNadia(text, opts) {
    if (!conv) return;
    opts = opts || {};
    var html;
    if (text === '[RANGE_THUMBS]') {
      html = bubble('nadia', rangeThumbs(), { html: true });
    } else if (/^\[.+\]$/.test(text)) {
      // A bracketed list represents quick-reply chips
      try {
        var arr = JSON.parse(text.replace(/'/g, '"'));
        html = bubble('nadia', renderOptions(arr), { html: true });
      } catch (e) {
        html = bubble('nadia', text);
      }
    } else {
      html = bubble('nadia', text);
    }
    conv.scrollEl.insertAdjacentHTML('beforeend', html);
    scrollToBottom();
  }

  function appendUser(text) {
    if (!conv) return;
    conv.scrollEl.insertAdjacentHTML('beforeend', bubble('user', text));
    scrollToBottom();
  }

  function appendTyping() {
    if (!conv) return;
    var html = '<div class="sc-conv-typing"><span></span><span></span><span></span></div>';
    conv.scrollEl.insertAdjacentHTML('beforeend', html);
    scrollToBottom();
  }
  function removeTyping() {
    if (!conv) return;
    var t = conv.scrollEl.querySelector('.sc-conv-typing');
    if (t) t.remove();
  }

  function scrollToBottom() {
    if (!conv) return;
    setTimeout(function () { conv.scrollEl.scrollTop = conv.scrollEl.scrollHeight; }, 50);
  }

  /* Stream messages with a small delay between each, like a typing pace */
  function speak(messages, done) {
    if (!messages || !messages.length) { if (done) done(); return; }
    appendTyping();
    var first = messages.shift();
    var delay = Math.min(900, 350 + first.length * 12);
    setTimeout(function () {
      removeTyping();
      appendNadia(first);
      if (messages.length) speak(messages, done);
      else if (done) done();
    }, delay);
  }

  /* ============================================================
     Step runner
     ============================================================ */

  function currentStep() {
    if (!conv) return null;
    return CATALOGUE_STEPS[conv.step] || null;
  }

  function askCurrentStep() {
    var step = currentStep();
    if (!step) return;
    var msgs = step.ask(conv);
    // Disable input while Nadia speaks
    if (conv.inputEl) conv.inputEl.disabled = true;
    speak(msgs.slice(), function () {
      if (conv && conv.inputEl) {
        conv.inputEl.disabled = false;
        conv.inputEl.focus();
      }
    });
  }

  function handleUserInput(input) {
    if (!conv || !input) return;
    appendUser(input);

    if (conv.mode === 'd2c') {
      /* If we just offered the catalogue, the next message is the
         answer. Branch on yes/no before any other interpretation. */
      if (conv.awaitingCatalogueYesNo) {
        conv.awaitingCatalogueYesNo = false;
        if (isAffirmative(input)) {
          speak(["Wonderful. Just a few details and I'll get it out to you."], function () {
            switchToCatalogueMidStream();
          });
          return;
        }
        if (isNegative(input)) {
          speak(["No problem at all. Tell me what else would help."]);
          return;
        }
        /* Otherwise, treat as a normal d2c message — the catalogue
           offer simply lapses. */
      }
      conv.d2cTurns = (conv.d2cTurns || 0) + 1;
      handleD2CTurn(input);
      return;
    }

    var step = currentStep();
    if (!step || step.terminal) return;
    var result = step.handle(input, conv);
    if (result && result.messages && result.messages.length) {
      speak(result.messages.slice(), function () {
        if (result.advance) {
          conv.step += 1;
          askCurrentStep();
        }
      });
    } else if (result && result.advance) {
      conv.step += 1;
      askCurrentStep();
    }
  }

  /* General D2C scripted replies. In production this is replaced with
     a fetch() to the Nadia backend proxy, sending conversation history. */
  function handleD2CTurn(input) {
    var lower = input.toLowerCase();
    var reply;
    if (/coco\b|coco /.test(lower) && !/cocobella/.test(lower)) {
      reply = "The Coco is the most-loved of the range. Hardwood frame, individual coil sprung, feather and down cushions. Three sizes from 2-seater to 3.5, and a fully bespoke version. From £5,895 in the standard fabric range. Tell me about your room — how big is it, what's the light like — and I can think about which size sits best.";
    } else if (/cocobella/.test(lower)) {
      reply = "Cocobella is Coco's sister at John Lewis. Same design language, knife-edge cushions, fishmouth-sprung steel base. UK exclusive to JL with 0% finance. Would you like me to compare the two for you?";
    } else if (/barton/.test(lower)) {
      reply = "Barton is the deepest seat we make. Designed for properly settling in. Generous arms, deep back. From £6,720. Are you thinking of a snug or a more open living room?";
    } else if (/henrietta/.test(lower)) {
      reply = "Henrietta is our heritage piece, modelled on a Victorian original. Roll arms, shaped back. From £4,920. Available in 1-seater and 2.5-seater only. Is it for a more formal room?";
    } else if (/isobel/.test(lower)) {
      reply = "The Isobel is slimmer-framed, with an elegant curve. From £4,150. Sizes 1 through 4. What's the space like?";
    } else if (/felix/.test(lower)) {
      reply = "Felix is our cleanest, most modern silhouette. Fixed back, family-ready. From £3,895. There's a Sophie Conran version sold direct, and a separate Felix at John Lewis. Which one would you like to know about?";
    } else if (/sophia/.test(lower)) {
      reply = "Sophia is fixed-cover, mahogany leg. From £3,450. Tell me about the people who'll be using it most.";
    } else if (/lead time|how long|when can|delivery time|arrive/.test(lower)) {
      reply = "Around 8 weeks from order in the UK, often sooner if we have a stock base. The US is 10 to 14 weeks at the moment. If timing's tight, I can show you what's ready now in our quick-ship programme.";
    } else if (/price|cost|how much|budget/.test(lower)) {
      reply = "The starting points across the range: Sophia from £3,450, Felix from £3,895, Isobel from £4,150, Henrietta from £4,920, Coco from £5,895, Barton from £6,720. Bespoke fabrics and sizes change those numbers. What's the budget you're working with?";
    } else if (/fabric|sample|swatch/.test(lower)) {
      reply = "Free fabric samples, posted within a week, up to five per request. We have plain linens, stripes, florals, velvets, and a full bespoke COM service. What kind of fabric direction are you drawn to?";
    } else if (/care|wash|clean|spill|stain/.test(lower)) {
      reply = "Most of our cotton-linen plains and stripes are machine-washable at 30°C. Florals and velvets are typically dry-clean only. Loose-cover construction means the cover comes off the sofa for cleaning. Want me to talk through fabric care for a specific one?";
    } else if (/room|living room|snug|reception/.test(lower)) {
      reply = "Tell me a little about it — rough size in metres, what direction the light comes from, any colours you've already committed to. I can think about which range and fabric direction will work best.";
    } else if (/sustain|chemical|natural|fr\b|fire|retardant/.test(lower)) {
      reply = "Every UK sofa must meet fire safety regulations and ours do. On Coco and Barton, our lowest-chemical models, we're developing a build option that uses a wool fire barrier in place of a chemically treated interliner. Sophie has written a letter explaining how it works.";
    } else if (/catalogue|brochure/.test(lower)) {
      /* If they've already submitted, acknowledge. If not, offer to
         take their details now without leaving the conversation. */
      if (isCatalogueAlreadyRequested()) {
        reply = "I've got your catalogue request on file, so the printed book will be with you in a week or so. While we're here, what would be useful to talk through?";
      } else {
        reply = "Of course. The printed catalogue is gorgeous and has every range, fabric and finish in one place. Shall I take your details now and send one out?";
        conv.catalogueOffered = true;
        conv.awaitingCatalogueYesNo = true;
      }
    } else if (/visit|showroom|see in person|come and see/.test(lower)) {
      reply = "Our workshop in Long Eaton is open for showroom visits by appointment. We can arrange a designer to walk you through the range and bring out the fabrics that are most relevant. Would you like me to set that up?";
    } else if (/bed|linen|sheet|duvet|pillow/.test(lower)) {
      reply = "The Scalloped bed linen comes in eight colours, from £73.50 for a single duvet cover. Hand-finished scalloped edges, Sophie's own design. Want me to walk you through the colour options?";
    } else if (/portmeirion|tableware|china|plate|mug/.test(lower)) {
      reply = "Sophie's collection with Portmeirion has been running for twenty years. Six collections including White Porcelain, Floret, Lavandula, Honey Pot. All sold here on the site, drop-shipped from Portmeirion. Which collection caught your eye?";
    } else if (/thanks|thank you|cheers|brilliant|lovely|great/.test(lower)) {
      reply = "You're very welcome. Is there anything else I can help you think through?";
    } else if (/^bye|^goodbye|^that's all|^that is all/.test(lower)) {
      reply = "Take your time. If you'd like to talk again, I'll be here. Or come and see us at the workshop any time.";
    } else {
      reply = "Tell me a bit more about your room or what you've got in mind, and I can point you in the right direction.";
    }

    /* Proactive catalogue offer. Surfaces once per conversation, after
       the 4th turn, only if Nadia hasn't already offered AND the
       customer hasn't already submitted. Appended to whatever reply
       was about to be given. Keeps the offer naturally embedded
       rather than interrupting. */
    var shouldOfferProactively = !conv.catalogueOffered
      && !isCatalogueAlreadyRequested()
      && (conv.d2cTurns || 0) >= 4
      && conv.mode === 'd2c';

    if (shouldOfferProactively) {
      reply += "\n\nBy the way — would you like me to send you the printed catalogue while we're chatting? It has everything in one place to mull over.";
      conv.catalogueOffered = true;
      conv.awaitingCatalogueYesNo = true;
    }

    speak([reply]);
  }

  /* ============================================================
     Panel UI
     ============================================================ */

  function buildPanel(mode) {
    var modeClass = 'sc-inline-panel-' + mode;
    return '' +
      '<div class="sc-inline-panel-inner ' + modeClass + '">' +
        '<div class="sc-inline-panel-head">' +
          '<div class="sc-inline-panel-meta">' +
            '<div class="sc-inline-panel-avatar">N</div>' +
            '<div>' +
              '<div class="sc-inline-panel-name">Nadia</div>' +
              '<div class="sc-inline-panel-sub">' +
                (mode === 'catalogue' ? 'Sophie Conran catalogue' : 'Sophie Conran') +
              '</div>' +
            '</div>' +
          '</div>' +
          '<button class="sc-inline-panel-close" type="button" data-sc-conv-close aria-label="Close">×</button>' +
        '</div>' +
        '<div class="sc-conv-scroll" data-sc-conv-scroll></div>' +
        '<div class="sc-conv-input-row">' +
          '<input class="sc-conv-input" type="text" placeholder="Type your message" data-sc-conv-input autocomplete="off" />' +
          '<button class="sc-conv-send" type="button" data-sc-conv-send aria-label="Send">&rarr;</button>' +
        '</div>' +
        '<div class="sc-conv-terminal-cta" hidden>' +
          '<button type="button" class="sc-btn sc-btn-text" data-sc-conv-restart>Start a new conversation &rsaquo;</button>' +
        '</div>' +
      '</div>';
  }

  function openPanel(mode) {
    var panelEl = document.getElementById('sc-inline-panel');
    if (!panelEl) {
      // Create one. Place it immediately after the hero section so the panel
      // expands inline below the visitor's current focus.
      var hero = document.querySelector('.sc-hero-lifestyle, .sc-bl-hero, .sc-pm-hero, .sc-partner-hero, .sc-stock-banner');
      var main = document.querySelector('main');
      panelEl = document.createElement('div');
      panelEl.id = 'sc-inline-panel';
      panelEl.className = 'sc-inline-panel';
      if (hero && hero.parentNode) {
        hero.parentNode.insertBefore(panelEl, hero.nextSibling);
      } else if (main && main.firstChild) {
        main.insertBefore(panelEl, main.firstChild);
      } else if (main) {
        main.appendChild(panelEl);
      } else {
        document.body.appendChild(panelEl);
      }
    }
    panelEl.innerHTML = buildPanel(mode);
    panelEl.hidden = false;
    panelEl.classList.add('is-open');

    conv = {
      mode: mode,
      step: 0,
      request: loadRequest(),
      panelEl: panelEl,
      scrollEl: panelEl.querySelector('[data-sc-conv-scroll]'),
      inputEl: panelEl.querySelector('[data-sc-conv-input]'),
      d2cTurns: 0,
      catalogueOffered: false,
      awaitingCatalogueYesNo: false
    };

    bindPanel(panelEl);

    // Smooth scroll the panel into view
    setTimeout(function () {
      panelEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);

    // Kick off the conversation
    if (mode === 'catalogue') {
      /* Skip the form for returning customers. If they already
         submitted (their record carries a submittedAt), drop straight
         into D2C chat with a welcome-back message rather than asking
         for name and address again. */
      if (isCatalogueAlreadyRequested()) {
        conv.mode = 'd2c';
        var name = firstName(conv.request.name);
        speak([
          "Welcome back, " + name + ". I've got your catalogue request on file.",
          "How can I help today?"
        ], function () { conv.inputEl.focus(); });
      } else {
        /* Fresh catalogue request: brief welcoming line, then the form. */
        speak([
          "Lovely. The catalogue will land with you in about a week — let me take a few details."
        ], function () {
          askCurrentStep();
        });
      }
    } else {
      // D2C: show the opening lines, then enable input
      speak(D2C_OPENING.slice(), function () {
        conv.inputEl.focus();
      });
    }
  }

  function closePanel() {
    var panelEl = document.getElementById('sc-inline-panel');
    if (panelEl) {
      panelEl.classList.remove('is-open');
      panelEl.hidden = true;
      panelEl.innerHTML = '';
    }
    conv = null;
  }

  function bindPanel(panelEl) {
    if (panelEl.dataset.scBound === '1') return;
    panelEl.dataset.scBound = '1';
    panelEl.addEventListener('click', function (e) {
      var pick = e.target.closest('[data-sc-conv-pick]');
      if (pick) {
        var val = pick.getAttribute('data-sc-conv-pick');
        // Disable other chips/thumbs in same wrapper to prevent double-tap
        var wrap = pick.parentElement;
        if (wrap) wrap.querySelectorAll('[data-sc-conv-pick]').forEach(function (b) { b.disabled = true; });
        handleUserInput(val);
        return;
      }
      if (e.target.closest('[data-sc-conv-send]')) {
        sendInput();
        return;
      }
      if (e.target.closest('[data-sc-conv-close]')) {
        closePanel();
        return;
      }
      if (e.target.closest('[data-sc-conv-restart]')) {
        var mode = conv ? conv.mode : 'catalogue';
        closePanel();
        openPanel(mode);
        return;
      }
    });
    panelEl.addEventListener('keydown', function (e) {
      if (e.target.matches('[data-sc-conv-input]') && e.key === 'Enter') {
        e.preventDefault();
        sendInput();
      }
    });
  }

  function sendInput() {
    if (!conv) return;
    var val = conv.inputEl.value.trim();
    if (!val) return;
    conv.inputEl.value = '';
    handleUserInput(val);
  }

  /* ============================================================
     Entry points / global wiring
     ============================================================ */

  SC.catalogue = {
    open: function () { openPanel('catalogue'); },
    openD2C: function () { openPanel('d2c'); },
    isAlreadySubmitted: isCatalogueAlreadyRequested,
    getRequest: loadRequest,
    reset: function () { localStorage.removeItem(STORAGE_KEY); }
  };

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-sc-catalogue-open]')) {
      e.preventDefault();
      SC.catalogue.open();
    }
    if (e.target.closest('[data-sc-nadia-inline-open]')) {
      e.preventDefault();
      SC.catalogue.openD2C();
    }
  });

})(window, document);
